/**
 * TestaferrosPanel — directores/dignatarios/apoderados sincronizados del
 * Registro Público (registry_officers), comparados contra los dueños
 * efectivos declarados (OwnersPanel) para detectar posibles testaferros:
 * personas que firman ante el Registro Público sin figurar como dueños, y
 * dueños efectivos que no tienen firma registrada. Solo lectura — los
 * directivos se sincronizan por el barrido del Registro Público, no aquí.
 * Sigue el estilo de fila de OwnersPanel. Cadenas es-PA únicamente.
 */
import { useQuery } from '@tanstack/react-query';
import type { EntityMode } from '@cumpli/shared';
import { api, type OwnerUi } from '../lib/api';
import { detectarBrechasTestaferros } from '../lib/testaferros';
import { Card, Pill, Skeleton } from './ui';
import { PersonaIcon } from './icons';

export default function TestaferrosPanel({
  expedienteId,
  owners,
  isLoadingOwners,
  sector,
}: {
  expedienteId: string;
  owners: OwnerUi[];
  isLoadingOwners: boolean;
  sector: EntityMode;
}) {
  const { data: oficiales, isLoading: isLoadingOficiales } = useQuery({
    queryKey: ['expedientes', expedienteId, 'registry-officers'],
    queryFn: () => api.listRegistryOfficers(expedienteId),
  });

  // No calculamos ni mostramos brechas hasta que AMBAS consultas (dueños y
  // directivos) hayan asentado — de lo contrario, si los directivos llegan
  // primero, cada firmante se marca momentáneamente como testaferro con una
  // lista de dueños vacía (falso positivo de cumplimiento).
  const isLoading = isLoadingOficiales || isLoadingOwners;
  const brechas = isLoading
    ? { oficiales: [], duenosSinFirma: [] }
    : detectarBrechasTestaferros(owners, oficiales ?? [], sector);

  return (
    <Card>
      <h2 className="font-serif text-xl font-medium text-ink">
        Directivos y dignatarios (Registro Público)
      </h2>
      <p className="mt-1 text-sm text-ink-soft">
        Personas que figuran en el Registro Público como directores, dignatarios o apoderados de
        la sociedad. Se comparan con los dueños declarados para detectar posibles testaferros.
      </p>

      <div className="mt-5">
        {isLoading ? (
          <Skeleton className="mt-4 h-24 w-full rounded-xl" />
        ) : (oficiales ?? []).length === 0 ? (
          <div className="grid place-items-center gap-2 rounded-xl border border-dashed border-line-strong bg-canvas px-6 py-12 text-center">
            <p className="max-w-sm text-sm text-ink-soft">
              Aún no hay directivos del Registro Público para este expediente. Ejecute la
              consulta al Registro Público (barrido) para traer los miembros de la sociedad.
            </p>
          </div>
        ) : (
          <div className="grid gap-3">
            {brechas.oficiales.map((o) => (
              <div
                key={o.id}
                className="flex flex-wrap items-center gap-3 rounded-xl border border-line bg-panel px-4 py-3.5"
              >
                <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-tone-slate-bg text-tone-slate">
                  <PersonaIcon persona="natural" className="h-5 w-5" />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="truncate text-sm font-bold text-ink">{o.nombre}</span>
                    <Pill tone="slate">{o.cargo}</Pill>
                    {o.posicion && <Pill tone="slate">{o.posicion}</Pill>}
                    {o.firmaSinDueno && (
                      <Pill tone="amber">Firma pero no figura como dueño</Pill>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {brechas.duenosSinFirma.length > 0 && (
          <div className="mt-5">
            <div className="mb-2 text-sm font-semibold text-ink">
              Dueños efectivos que no figuran en el Registro Público:
            </div>
            <div className="grid gap-2.5">
              {brechas.duenosSinFirma.map((o) => (
                <div
                  key={o.id}
                  className="flex flex-wrap items-center gap-3 rounded-xl border border-line bg-panel px-4 py-3.5"
                >
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-tone-slate-bg text-tone-slate">
                    <PersonaIcon persona="natural" className="h-5 w-5" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="truncate text-sm font-bold text-ink">{o.name}</span>
                      <Pill tone="amber">Dueño efectivo sin firma</Pill>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}
